import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { formatCurrencyNoDecimals, formatPercentage } from '@/utils/formatters';
import { ExternalLink } from 'lucide-react';
import type { FundHolding } from '@/portal/holdings/types';

interface FundHoldingCardProps {
  fund: FundHolding;
  onViewDetails: (fundId: string) => void;
}

export function FundHoldingCard({ fund, onViewDetails }: FundHoldingCardProps) {
  const getStatusBadge = (status: string | null) => {
    if (!status) return <Badge variant="secondary">Unknown</Badge>;
    const statusLower = status.toLowerCase();
    if (statusLower === 'active' || statusLower === 'investing') {
      return <Badge>Active</Badge>;
    }
    if (statusLower === 'closed' || statusLower === 'liquidated') {
      return <Badge variant="secondary">{status}</Badge>;
    }
    return <Badge variant="outline">{status}</Badge>;
  };

  return (
    <div className="border rounded-lg p-6 bg-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold">
            {fund.display_name || fund.name}
          </h3>
          <p className="text-sm text-muted-foreground">
            Vintage {fund.vintage_year || 'N/A'} • {fund.base_currency} •{' '}
            {fund.status || 'Active'}
          </p>
        </div>
        {getStatusBadge(fund.status)}
      </div>

      <div className="grid grid-cols-3 md:grid-cols-4 gap-4 mb-4">
        <div>
          <p className="text-sm text-gray-500">Commitment</p>
          <p className="font-semibold">
            {formatCurrencyNoDecimals(
              fund.commitment_amount,
              fund.base_currency
            )}
          </p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Called</p>
          <p className="font-semibold">
            {formatCurrencyNoDecimals(fund.total_called, fund.base_currency)}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Prepayment Pool</p>
          <p className="font-semibold">
            {formatCurrencyNoDecimals(fund.prepayment_pool, fund.base_currency)}
          </p>
        </div>
      </div>

      <div className="mb-4">
        <div className="flex justify-between text-sm mb-1">
          <span>Capital Called</span>
          <span>{formatPercentage(fund.call_percentage)}</span>
        </div>
        <Progress value={fund.call_percentage * 100} className="h-2" />
      </div>

      <div className="flex justify-end items-center">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onViewDetails(fund.id)}
        >
          <ExternalLink className="h-4 w-4 mr-1" />
          View Details
        </Button>
      </div>
    </div>
  );
}
